/**
 * Fases do Cronograma CIPAMIN (NR-22)
 * Comissão Interna de Prevenção de Acidentes na Mineração
 */

import { Fase } from '../types';
import { Users, FileText, Vote, CheckCircle, Award } from 'lucide-react';

export const criarFasesCipamin = (dataTerminoMandato: string): Fase[] => {
  const dataTermino = new Date(dataTerminoMandato);
  
  const calcularData = (diasAntes: number): Date => {
    const data = new Date(dataTermino);
    data.setDate(data.getDate() - diasAntes);
    return data;
  };

  return [
    // FASE 1: Formação da Comissão Eleitoral
    {
      id: 'fase1',
      titulo: 'Formação da Comissão Eleitoral',
      artigo: 'Art. 22.35.2 NR-22',
      prazoNR: '60 dias antes do término do mandato',
      data: calcularData(60),
      cor: 'bg-blue-600',
      icon: Users,
      acoes: [
        'Designar membros da comissão eleitoral entre os membros atuais da CIPAMIN',
        'Definir presidente e secretário da comissão',
        'Elaborar cronograma detalhado do processo eleitoral',
        'Preparar documentação necessária para o processo'
      ]
    },
    
    // FASE 2: Comunicação ao Sindicato
    {
      id: 'fase2',
      titulo: 'Comunicação ao Sindicato',
      artigo: 'Art. 22.35.1.1 NR-22',
      prazoNR: '58 dias antes do término (após formação da comissão)',
      data: calcularData(58),
      cor: 'bg-green-600',
      icon: FileText,
      acoes: [
        'Comunicar início do processo eleitoral ao sindicato da categoria preponderante',
        'Utilizar meio eletrônico com confirmação de entrega',
        'Manter comprovante da comunicação',
        'Aguardar eventual manifestação do sindicato'
      ]
    },
    
    // FASE 3: Publicação do Edital de Convocação
    {
      id: 'fase3',
      titulo: 'Publicação do Edital de Convocação',
      artigo: 'Art. 22.35.3(a) NR-22',
      prazoNR: '45 dias antes do término (abertura de prazos para inscrições)',
      data: calcularData(45),
      cor: 'bg-purple-600',
      icon: FileText,
      acoes: [
        'Publicar edital em locais de fácil acesso e visualização',
        'Incluir todas as informações sobre o processo eleitoral',
        'Disponibilizar em meio físico ou eletrônico',
        'Divulgar amplamente entre todos os empregados'
      ]
    },
    
    // FASE 4: Período de Inscrições
    {
      id: 'fase4',
      titulo: 'Período de Inscrições',
      artigo: 'Art. 22.35.3(b) NR-22',
      prazoNR: '44 a 29 dias antes (15 dias corridos - mínimo exigido)',
      data: calcularData(44),
      dataFim: calcularData(29),
      cor: 'bg-yellow-600',
      icon: Users,
      acoes: [
        'Receber inscrições individuais de todos os empregados',
        'Fornecer comprovante de inscrição (físico ou eletrônico)',
        'Garantir liberdade de inscrição independente de setor',
        'Garantir estabilidade de emprego para todos os inscritos'
      ]
    },
    
    // FASE 5: Publicação da Lista de Candidatos
    {
      id: 'fase5',
      titulo: 'Publicação da Lista de Candidatos',
      artigo: 'Art. 22.35.3(e) NR-22',
      prazoNR: '28 dias antes (1 dia após término das inscrições)',
      data: calcularData(28),
      cor: 'bg-indigo-600',
      icon: FileText,
      acoes: [
        'Publicar relação completa dos empregados inscritos',
        'Divulgar em locais de fácil acesso e visualização',
        'Disponibilizar em meio físico ou eletrônico',
        'Verificar dados e informações dos candidatos'
      ]
    },
    
    // FASE 6: Eleição (Votação)
    {
      id: 'fase6',
      titulo: 'Eleição (Votação)',
      artigo: 'Art. 22.35.3(f) NR-22',
      prazoNR: '31 dias antes do término (mínimo exigido pela NR-22)',
      data: calcularData(31),
      cor: 'bg-orange-600',
      icon: Vote,
      acoes: [
        'Realizar eleição em dia normal de trabalho',
        'Respeitar horários de turnos para participação da maioria',
        'Garantir voto secreto',
        'Utilizar sistema que garanta segurança e confidencialidade'
      ]
    },
    
    // FASE 7: Apuração dos Votos
    {
      id: 'fase7',
      titulo: 'Apuração dos Votos',
      artigo: 'Art. 22.35.3(i) NR-22',
      prazoNR: '30 dias antes (1 dia após eleição)',
      data: calcularData(30),
      cor: 'bg-teal-600',
      icon: CheckCircle,
      acoes: [
        'Apurar votos em horário normal de trabalho',
        'Permitir acompanhamento de representantes da organização e empregados',
        'Facultar acompanhamento dos candidatos',
        'Elaborar ata de eleição e apuração com ordem decrescente de votos'
      ]
    },
    
    // FASE 8: Treinamento Obrigatório CIPAMIN
    {
      id: 'fase8',
      titulo: 'Treinamento Obrigatório CIPAMIN',
      artigo: 'Art. 22.36.1 NR-22',
      prazoNR: '15 dias antes do término (antes da posse) - Mínimo 20 horas',
      data: calcularData(15),
      cor: 'bg-pink-600',
      icon: Award,
      acoes: [
        'Promover treinamento para membros titulares e suplentes (mínimo 20 horas)',
        'Incluir conteúdo sobre legislação de mineração (NR-22)',
        'Abordar riscos específicos: soterramento, explosivos, gases, desmoronamento',
        'Incluir procedimentos de emergência em ambientes de mineração'
      ]
    },
    
    // FASE 9: Posse dos Membros Eleitos
    {
      id: 'fase9',
      titulo: 'Posse dos Membros Eleitos',
      artigo: 'Art. 22.34.7 NR-22',
      prazoNR: '1 dia após término do mandato anterior',
      data: new Date(dataTermino.getTime() + 24 * 60 * 60 * 1000), // +1 dia
      cor: 'bg-red-600',
      icon: Award,
      acoes: [
        'Empossar membros eleitos e designados',
        'Fornecer cópias das atas de eleição e posse',
        'Designar presidente (representante da organização)',
        'Eleição do vice-presidente (entre representantes dos empregados)'
      ]
    }
  ];
};
